import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { logListenerError } from 'features/firebase/services/listenerErrorLog';
import { listenForFirestoreCollectionSnapshots } from 'features/firebase/services/subscriptionService';
import { getActivityTasksCollectionPath } from 'features/questionnaires/services/documentService';
import type { TaskProgressDoc } from '../types/ScheduleTask';
import type { DbTask } from '@hub/common';

type Params = { userId: string; participationId: string };

export type ActivityTasksSubscriptionData = {
  isSnapshotReceived: boolean;
  tasks: TaskProgressDoc[];
};

export const activityTasksApi = createApi({
  reducerPath: 'activityTasksApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['activityTasks'],
  endpoints: builder => ({
    activityTasksSubscription: builder.query<
      ActivityTasksSubscriptionData,
      Params
    >({
      providesTags: ['activityTasks'],
      keepUnusedDataFor: 3600,
      queryFn() {
        return { data: { isSnapshotReceived: false, tasks: [] } };
      },
      async onCacheEntryAdded(
        params,
        // eslint-disable-next-line @typescript-eslint/unbound-method -- it's how it is in Redux docs
        { updateCachedData, cacheEntryRemoved }
      ) {
        await listenForFirestoreCollectionSnapshots<DbTask>(
          cacheEntryRemoved,
          getActivityTasksCollectionPath(params.userId, params.participationId),
          tasks => {
            updateCachedData(() => ({
              isSnapshotReceived: true,
              // The join consumes only the progress fields; the full doc
              // carries binary contextData (capacitor Bytes) that must not
              // enter the Redux store.
              tasks: tasks.map(({ activityName, status }) => ({
                activityName,
                status,
              })),
            }));
          },
          error => {
            logListenerError('Unable to subscribe to activity tasks', error);
            // Resolve the loading gate so home/schedule fall through to the
            // last-known (or empty) schedule instead of an indefinite spinner.
            updateCachedData(data => {
              data.isSnapshotReceived = true;
            });
          }
        );
      },
    }),
  }),
});

export const { useActivityTasksSubscriptionQuery } = activityTasksApi;
